import { useInboxes } from "hooks/queries/use-inboxes";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  clearLegacyTeamScopeFromLocalStorage,
  getInboxScopeFromLocalStorage,
  getLegacyTeamScopeFromLocalStorage,
  persistInboxScope,
  selectedInboxSelector,
} from "redux/features/conversations";
import { useAppSelector } from "redux/hooks";
import InboxDomain from "entities/domain/inbox";
import {
  InboxScope,
  applyInboxScopeToSearchParams,
  inboxScopeForLegacyTeamScope,
  isInboxScopeUnavailable,
  legacyTeamScopeForSearch,
  parseInboxScopeFromSearch,
  resolveInboxScopeAgainstInboxes,
  searchHasLegacyTeamScope,
  searchNamesInboxScope,
  trackLegacyTeamScopeResolved,
} from "util/inbox-scope";

/**
 * Rewrites a Release 1 link (`?team=` / `?no_team=true`) to the `?inbox=` it
 * now means, once the Inbox list says which Inbox that is.
 *
 * Done without going through react-router for the same reason as
 * canonicalizeTabSearchParam — setSearchParams is a full router navigation.
 * history.state is preserved so react-router's history index survives. The
 * router's copy keeps the old params until the next navigation, which is
 * harmless: every reader resolves them to the same Inbox, and every scope
 * write drops them.
 */
const canonicalizeLegacyTeamScopeSearchParams = (
  inboxes: InboxDomain[]
): void => {
  const url = new URL(window.location.href);

  if (!searchHasLegacyTeamScope(url.search)) {
    return;
  }

  const scope = parseInboxScopeFromSearch(url.search, inboxes);

  if (scope === null) {
    return;
  }

  // Counted here, not where the URL is parsed: this runs once per link, while
  // the parsers run on every render.
  const legacy = legacyTeamScopeForSearch(url.search);

  if (legacy) {
    trackLegacyTeamScopeResolved("url", legacy);
  }

  const next = applyInboxScopeToSearchParams(url.searchParams, scope);
  url.search = next.toString();
  window.history.replaceState(window.history.state, "", url);
};

/**
 * The Inbox scope the inbox is currently looking at, or `null` while a Release
 * 1 link waits for the Inbox list: there is nothing honest to request before
 * the Inbox it names is known, so lists wait instead of widening to All.
 *
 * The URL is read first and Redux is only a fallback, because the effects that
 * mirror the scope params into `selectedInbox` live in
 * DesktopTabsArea/MobileTopBar — and MobileTopBar unmounts once a conversation
 * is open, so a deep link straight to a conversation on mobile never populates
 * Redux. Redux still matters in the other direction: it survives navigation
 * that drops the params.
 *
 * An Inbox that has left the active list (archived, retired, another
 * merchant's) stays the scope: the list asks for it and gets nothing, and
 * useIsInboxScopeUnavailable says so. It never becomes All or General
 * Enquiries behind the user's back.
 */
export const useInboxScope = (): InboxScope | null => {
  const location = useLocation();
  const { data: merchant } = useMerchant();
  const inboxesQuery = useInboxes(merchant?.id);
  const selectedInbox = useAppSelector(selectedInboxSelector);
  const inboxes = inboxesQuery.isSuccess ? inboxesQuery.data : undefined;
  const scopeFromUrl = parseInboxScopeFromSearch(location.search, inboxes);

  if (scopeFromUrl === null) {
    return null;
  }

  return searchNamesInboxScope(location.search) ? scopeFromUrl : selectedInbox;
};

/**
 * Whether the scope being looked at names an Inbox that is no longer active.
 * False until the Inbox list is known, so a deep link is never reported
 * unavailable before it could be checked.
 */
export const useIsInboxScopeUnavailable = (): boolean => {
  const { data: merchant } = useMerchant();
  const inboxesQuery = useInboxes(merchant?.id);
  const scope = useInboxScope();

  return (
    scope !== null &&
    inboxesQuery.isSuccess &&
    isInboxScopeUnavailable(scope, inboxesQuery.data)
  );
};

/**
 * Restores the scope the inbox opens on when the URL does not name one:
 * explicit URL, then the per-device saved choice, then All. A Release 1 saved
 * Team choice is carried over to that Team's Inbox here, the first time the
 * Inbox list is known for the merchant it belongs to.
 *
 * Runs at most once per mount. `all` is encoded as the *absence* of a scope
 * param, so a second run could not tell "the user chose All" from "nobody has
 * chosen yet" and would keep dragging them back into a stored Inbox.
 *
 * Also rewrites Release 1 links whenever the URL carries one.
 */
export const useApplyDefaultInboxScope = (): void => {
  const location = useLocation();
  const [currentSearchParameters, setSearchParams] = useSearchParams();
  const { data: merchant } = useMerchant();
  const inboxesQuery = useInboxes(merchant.id);
  const hasApplied = useRef<boolean>(false);

  useEffect(() => {
    if (inboxesQuery.isSuccess) {
      canonicalizeLegacyTeamScopeSearchParams(inboxesQuery.data);
    }
  }, [location.search, inboxesQuery.isSuccess, inboxesQuery.data]);

  useEffect(() => {
    if (hasApplied.current || !inboxesQuery.isSuccess) {
      return;
    }

    hasApplied.current = true;

    const inboxes = inboxesQuery.data;
    const legacy = getLegacyTeamScopeFromLocalStorage();

    // Carried over only when it names one of this merchant's Inboxes: a Team
    // choice made on another merchant stays until that merchant is opened.
    if (legacy) {
      const carriedOver = inboxScopeForLegacyTeamScope(legacy, inboxes);

      if (carriedOver.kind === "inbox") {
        persistInboxScope(carriedOver);
        clearLegacyTeamScopeFromLocalStorage();
        // Counted only here, once: a choice left for another merchant is
        // read again on every mount and is not a resolution.
        trackLegacyTeamScopeResolved("saved", legacy);
      }
    }

    if (searchNamesInboxScope(currentSearchParameters)) {
      return;
    }

    const stored = getInboxScopeFromLocalStorage();

    if (!stored) {
      return;
    }

    // Effective only. A stored Inbox that has since been retired resolves to
    // All for this render and is deliberately not written back, so returning
    // to a merchant where it still exists restores it.
    const resolved = resolveInboxScopeAgainstInboxes(stored, inboxes);

    if (resolved.kind === "all") {
      return;
    }

    setSearchParams(
      applyInboxScopeToSearchParams(currentSearchParameters, resolved),
      { replace: true }
    );
  }, [merchant, inboxesQuery.isSuccess, inboxesQuery.data]);
};
